/**
 * Tela /stores/:storeId (TH-53163 / M1.E4.S4.1).
 *
 * - Cabeçalho com detalhes da loja
 * - Lista de TVs com status online/offline (last_seen_at < 5min)
 * - Botão "Nova TV" → modal → após criar, abre modal grande com QR
 * - QR baixável como PNG
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Pagination } from "@/components/ui/Pagination";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/hooks/useAuth";
import { ActivationQRDialog } from "@/pages/store-detail/ActivationQRDialog";
import { NewTVDialog } from "@/pages/store-detail/NewTVDialog";

interface Store {
  id: string;
  name: string;
  code: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  active: boolean;
  tv_count: number;
}

interface TV {
  id: string;
  name: string;
  area_id: string | null;
  app_version: string | null;
  last_seen_at: string | null;
  created_at: string;
}

interface Area {
  id: string;
  name: string;
}

interface PagedTVs {
  data: TV[];
  meta: { page: number; per_page: number; total: number };
}

interface PagedAreas {
  data: Area[];
}

const PER_PAGE = 20;
const ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

const dateTimeFmt = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

async function fetchStore(id: string): Promise<Store> {
  const res = await fetch(`/api/stores/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as Store;
}

async function fetchTVs(storeId: string, page: number): Promise<PagedTVs> {
  const res = await fetch(
    `/api/stores/${storeId}/tvs?page=${page}&per_page=${PER_PAGE}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedTVs;
}

async function fetchAreas(): Promise<Area[]> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch(`/api/areas?per_page=100`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as PagedAreas;
  return body.data;
}

function isOnline(lastSeen: string | null): boolean {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < ONLINE_THRESHOLD_MS;
}

export function StoreDetailPage() {
  const { storeId } = useParams({ from: "/stores/$storeId" });
  const { user } = useAuth();
  const canManage =
    user?.role === "super_admin" || user?.role === "tenant_admin";

  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [qrTV, setQrTV] = useState<{ id: string; name?: string } | null>(null);

  const storeQ = useQuery({
    queryKey: ["store", storeId],
    queryFn: () => fetchStore(storeId),
    staleTime: 60_000,
    enabled: !!user,
  });

  const tvsQ = useQuery({
    queryKey: ["tvs", storeId, page],
    queryFn: () => fetchTVs(storeId, page),
    staleTime: 30_000,
    enabled: !!user,
  });

  const areasQ = useQuery({
    queryKey: ["areas", "all"],
    queryFn: fetchAreas,
    staleTime: 5 * 60_000,
    enabled: !!user,
  });

  const areaNameById = new Map(
    (areasQ.data ?? []).map((a) => [a.id, a.name]),
  );

  const title = storeQ.data?.name ?? (storeQ.isLoading ? "Carregando…" : "Loja");
  const breadcrumb = storeQ.data
    ? `OPERAÇÃO / LOJAS / ${storeQ.data.name.toUpperCase()}`
    : "OPERAÇÃO / LOJAS";

  return (
    <RequireAuth>
      <AppLayout breadcrumb={breadcrumb} title={title}>
        <div className="mx-auto max-w-5xl">
          {storeQ.isLoading ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              carregando…
            </p>
          ) : storeQ.error ? (
            <p className="text-sm text-[var(--color-destructive)]">
              erro ao carregar a loja
            </p>
          ) : storeQ.data ? (
            <Card className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Field label="Código" value={storeQ.data.code} />
              <Field label="Cidade" value={storeQ.data.city} />
              <Field label="UF" value={storeQ.data.state} />
              <Field
                label="Status"
                value={storeQ.data.active ? "Ativa" : "Inativa"}
              />
            </Card>
          ) : null}

          <section className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">TVs</h2>
              {canManage ? (
                <Button onClick={() => setCreating(true)}>Nova TV</Button>
              ) : null}
            </div>

            <div className="mt-4">
              <DataTable<TV>
                columns={[
                  {
                    key: "name",
                    header: "Nome",
                    cell: (tv) => (
                      <Link
                        to="/tvs/$tvId"
                        params={{ tvId: tv.id }}
                        className="font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] hover:underline"
                      >
                        {tv.name}
                      </Link>
                    ),
                  },
                  {
                    key: "area_id",
                    header: "Área",
                    cell: (tv) => (
                      <span className="text-[var(--color-muted-foreground)]">
                        {tv.area_id ? (areaNameById.get(tv.area_id) ?? "—") : "—"}
                      </span>
                    ),
                  },
                  {
                    key: "status",
                    header: "Status",
                    cell: (tv) => (
                      <StatusPill tone={isOnline(tv.last_seen_at) ? "success" : "muted"}>
                        {isOnline(tv.last_seen_at) ? "Online" : "Offline"}
                      </StatusPill>
                    ),
                  },
                  {
                    key: "last_seen_at",
                    header: "Última conexão",
                    mono: true,
                    cell: (tv) => (
                      <span className="text-[var(--color-muted-foreground)]">
                        {tv.last_seen_at
                          ? dateTimeFmt.format(new Date(tv.last_seen_at))
                          : "—"}
                      </span>
                    ),
                  },
                  {
                    key: "app_version",
                    header: "App",
                    cell: (tv) => (
                      <span className="text-[var(--color-muted-foreground)]">
                        {tv.app_version ?? "—"}
                      </span>
                    ),
                  },
                  ...(canManage
                    ? [
                        {
                          key: "actions",
                          header: "",
                          align: "right" as const,
                          cell: (tv: TV) => (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setQrTV({ id: tv.id, name: tv.name })}
                            >
                              QR
                            </Button>
                          ),
                        },
                      ]
                    : []),
                ] satisfies DataTableColumn<TV>[]}
                rows={tvsQ.isLoading || tvsQ.error ? [] : (tvsQ.data?.data ?? [])}
                rowKey={(tv) => tv.id}
                emptyMessage={
                  tvsQ.isLoading
                    ? "carregando…"
                    : tvsQ.error
                      ? "erro ao carregar"
                      : "nenhuma TV cadastrada"
                }
              />
            </div>

            {tvsQ.data && tvsQ.data.meta.total > PER_PAGE ? (
              <div className="mt-4">
                <Pagination
                  page={page}
                  perPage={PER_PAGE}
                  total={tvsQ.data.meta.total}
                  onPageChange={setPage}
                />
              </div>
            ) : null}
          </section>
        </div>

        {canManage ? (
          <NewTVDialog
            open={creating}
            onOpenChange={setCreating}
            storeId={storeId}
            areas={areasQ.data ?? []}
            onCreated={(tvId) => setQrTV({ id: tvId })}
          />
        ) : null}

        {qrTV ? (
          <ActivationQRDialog
            open
            onOpenChange={(o) => {
              if (!o) setQrTV(null);
            }}
            tvId={qrTV.id}
            tvName={qrTV.name}
          />
        ) : null}
      </AppLayout>
    </RequireAuth>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <p className="text-xs uppercase text-[var(--color-muted-foreground)]">
        {label}
      </p>
      <p className="mt-1 text-sm">{value ?? "—"}</p>
    </div>
  );
}
